import React, { forwardRef } from "react";
import { MenuItem, type MenuItemProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { cssLengthToPx } from "@CubeComponents/utils/cssLengthToPx";

// Undestructured props are forwarded to MUI's `MenuItem` via `...rest`. The spacing
// props accept either a bare pixel number or a CSS length string (see cssLengthToPx).
interface MenuItemComponentProps extends Omit<MenuItemProps, "sx"> {
  width?: string | number;
  height?: string | number;
  paddX?: string | number;
  paddY?: string | number;
  fontSize?: string | number;
  color?: string;
  sx?: SxProps<Theme>;
}

/**
 * MenuItemComponent
 * Unifies padding, height, font-size, and "inherit" font family.
 * For submenu anchors, ref is forwarded to MUI MenuItem.
 */
const MenuItemComponent = forwardRef(function MenuItemComponent(
  {
    children,
    onClick,
    disabled = false,
    title,
    width = "auto",
    height = "3rem",
    paddX = 16,
    paddY = 8,
    fontSize = "0.875rem",
    color = "#192048",
    sx = {},
    ...rest
  }: MenuItemComponentProps,
  ref: React.Ref<HTMLElement>,
) {
  // Normalize numeric / "Npx" / "Nrem" inputs to a px number, then emit rem so the
  // sx string honors the user's root font-size scaling.
  const paddXpx = (() => {
    const v = cssLengthToPx(paddX);
    return Number.isFinite(v) ? v : 16;
  })();
  const paddYpx = (() => {
    const v = cssLengthToPx(paddY);
    return Number.isFinite(v) ? v : 8;
  })();

  return (
    <MenuItem
      ref={ref as React.Ref<HTMLLIElement>}
      disabled={disabled}
      onClick={onClick}
      title={title}
      sx={
        {
          height,
          px: `${paddXpx / 16}rem`,
          py: `${paddYpx / 16}rem`,
          fontSize,
          color,
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          width,
          fontFamily: "inherit",
          borderTop: "0.0625rem solid #ECF3FE",
          ...sx,
        } as SxProps<Theme>
      }
      {...rest}
    >
      {children}
    </MenuItem>
  );
});

export default MenuItemComponent;
